import type {
    Bag as BagImplementation,
    BagKeyDescriptor,
    BagValueType,
} from '@jet/environment';
import {
    inject,
    ObjectGraph,
} from '@jet/environment/dependencies/object-graph';
import { isSome, type Opt } from '@jet/environment/types/optional';
import { ObjectGraphType } from './object-graph-type';

type BagKey =
    | 'countryCode'
    | 'language-tag'
    | 'metrics'
    | 'ampClassicalAPIDomains/default';

// tslint:disable-next-line:variable-name
const BagKeys: Record<BagKey, BagValueType> = {
    countryCode: 'string',
    'language-tag': 'string',
    metrics: 'dictionary',
    'ampClassicalAPIDomains/default': 'string',
};

type SubscribedMode = 'undecided' | 'yes' | 'no';

export interface URLResolutionRule {
    'subscribed-mode'?: SubscribedMode;
    'host-suffix-whitelist'?: string[];
    'host-patterns'?: string[];
    'path-patterns'?: string[];
}

export enum BagTabIdentifier {}

export interface BagTab {
    id: BagTabIdentifier;
}

export class Bag {
    readonly bagImplementation: BagImplementation;

    constructor(bagImplementation: BagImplementation) {
        this.bagImplementation = bagImplementation;
        bagImplementation.registerBagKeys(Bag.allBagKeyDescriptors);
    }

    optionalString(bagKey: BagKey): Opt<string> {
        return this.bagImplementation.string(bagKey);
    }

    optionalDictionary(bagKey: BagKey): Opt<unknown> {
        return this.bagImplementation.dictionary(bagKey);
    }

    optionalArray(bagKey: BagKey): Opt<unknown> {
        return this.bagImplementation.array(bagKey);
    }

    // Key properties
    static ampClassicalAPIDomainsDefault(objectGraph: ObjectGraph): string {
        return Bag.string(
            'ampClassicalAPIDomains/default',
            '/api/classical/v8',
            objectGraph,
        );
    }

    static countryCode(objectGraph: ObjectGraph): string {
        return Bag.string('countryCode', 'us', objectGraph);
    }

    // A BCP 47 compliant language tag: https://tools.ietf.org/search/bcp47
    static languageTag(objectGraph: ObjectGraph): string {
        return Bag.string('language-tag', 'en-US', objectGraph);
    }

    // Utilities

    static get allBagKeyDescriptors(): BagKeyDescriptor[] {
        return Object.entries(BagKeys).map(([key, type]) => ({
            key,
            type,
        }));
    }

    static optionalString(
        bagKey: BagKey,
        objectGraph: ObjectGraph,
    ): Opt<string> {
        const bag = inject(ObjectGraphType.bag, objectGraph);
        return bag.optionalString(bagKey);
    }

    static string(
        bagKey: BagKey,
        fallbackString: string,
        objectGraph: ObjectGraph,
    ): string {
        const value = Bag.optionalString(bagKey, objectGraph);
        if (isSome(value)) {
            return value;
        }

        return fallbackString;
    }

    static optionalDictionary(
        bagKey: BagKey,
        objectGraph: ObjectGraph,
    ): Opt<unknown> {
        const bag = inject(ObjectGraphType.bag, objectGraph);
        return bag.optionalDictionary(bagKey);
    }

    static optionalArray(
        bagKey: BagKey,
        objectGraph: ObjectGraph,
    ): Opt<unknown> {
        const bag = inject(ObjectGraphType.bag, objectGraph);
        return bag.optionalArray(bagKey);
    }
}
